import type { Location } from "react-router-dom";

interface JwtPayload {
  exp: number;
}

const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  return payload.exp < Math.floor(Date.now() / 1000);
};

/**
 * Synchronously reports whether a non-expired auth token exists in storage.
 * Used by the home route to decide between rendering the landing immediately
 * (no token — logged-out visitor or crawler) and showing a spinner while
 * `useAuth` verifies a token that is probably present. Reads the same `"token"`
 * storage key used throughout the codebase (see `useAuth`, `baseClient`).
 */
export const hasStoredToken = (): boolean => {
  const token = localStorage.getItem("token");
  return !!token && !isTokenExpired(token);
};

export const isTokenExpiringSoon = (
  token: string,
  thresholdMinutes: number = 5,
): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  const thresholdTime = Math.floor(Date.now() / 1000) + thresholdMinutes * 60;
  return payload.exp < thresholdTime;
};

/**
 * Session policy for failed auth requests: the stored session only ends when
 * the server explicitly rejected the credentials (401/403) or the request
 * really timed out. Transient connectivity failures (no HTTP response, 5xx,
 * 429) return false so the client keeps its tokens and can retry once the
 * network returns, instead of logging the user out on every network blip.
 */
export const shouldEndSessionOnError = (error: unknown): boolean => {
  const { response, code } = (error ?? {}) as {
    response?: { status?: number };
    code?: string;
  };
  if (response?.status === 401 || response?.status === 403) {
    return true;
  }
  return code === "ECONNABORTED" || code === "ETIMEDOUT";
};

export const getTokenExpiration = (token: string): Date | null => {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  if (typeof payload.exp !== "number") return null;
  return new Date(payload.exp * 1000);
};

/**
 * Router state for sending an unauthenticated user to `/login` while
 * remembering where they were headed, so `useAuth` can return them there after
 * a successful login (instead of the `/dashboard` default). The full
 * pathname + query string is preserved so any filters/view state survive the
 * round-trip.
 */
export const loginRedirectState = (
  location: Pick<Location, "pathname" | "search">,
): { from: { pathname: string } } => ({
  from: { pathname: `${location.pathname}${location.search}` },
});
